import {
  type ScopeChangeRequestStore,
  type ListScopeChangeRequestsQuery,
} from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { type TableSortOrder } from '@/components/Table'
import { SelectFilter } from '@/infra/SelectFilter'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import {
  type FilterItem,
  FilterItemSource,
  type FilterStorage,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { omit, uniq } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { combineLatest, EMPTY, type Observable } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators'
import type { CursorPaged } from '@/infra/paging'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { toStream, tapAction } from '@/utils/mobx-util'
import { deepEqual } from '@/utils/object-util'
import { match } from '@/utils/pattern-match-util'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import {
  type ScopeChangeRequest,
  type ScopeChangeRequestStatus,
} from './ScopeChangeRequest'
import { type ScopeChangeRequestSortField } from './scope-change-request-table-columns'
import { type FetchJobsFn } from '@/features/jobs'
import type { AlertRetryWrapperFactory } from '@/features/shell'

/**
 * The status groups the requests can be filtered by.
 */
export type ScopeChangeRequestStatusFilterOption =
  'all' | 'reviewing' | 'awaiting' | 'accepted' | 'rejected'

export interface ScopeChangeRequestStatusFilter {
  count?: number
  type: ScopeChangeRequestStatusFilterOption
}

const ScopeChangeRequestStatusFilter = {
  none: {
    count: 0,
    type: 'all',
  } as ScopeChangeRequestStatusFilter,
  pending: {
    count: 0,
    type: 'reviewing',
  } as ScopeChangeRequestStatusFilter,
}

export interface ScopeChangeRequestFilter {
  workspaceIds: Id[]
  status: ScopeChangeRequestStatusFilter
}

/**
 * Field sort.
 */
export interface ScopeChangeRequestSort {
  field: ScopeChangeRequestSortField
  direction: TableSortOrder
}

export interface ScopeChangeRequestSearchFilter {
  workspaceIds: Id[]
  statuses: ScopeChangeRequestStatus[]
  sort: ScopeChangeRequestSort
}

export const ScopeChangeRequestSearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    workspaceIds: [],
    sort: { field: 'CreateTime', direction: 'asc' },
    statuses: [],
  } as ScopeChangeRequestSearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: ScopeChangeRequestSearchFilter) =>
    ScopeChangeRequestSearchFilter.equals(
      omit(ScopeChangeRequestSearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (
    left: Partial<ScopeChangeRequestSearchFilter>,
    right: Partial<ScopeChangeRequestSearchFilter>,
  ) => deepEqual(left, right),
}

/**
 * Search result.
 */
export interface ScopeChangeRequestSearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: ScopeChangeRequestSearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The current list of jobs contained in the searcher.
   */
  readonly scopeChangeRequests: readonly ScopeChangeRequest[]
}

export class ScopeChangeRequestsSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Backing field for the last search.
   */
  @observable.ref private accessor _lastSearch: ScopeChangeRequestSearchResult =
    {
      scopeChangeRequests: [],
      searchFilter: ScopeChangeRequestSearchFilter.none,
      filtersApplied: false,
    }

  readonly filters

  /**
   * Sorting.
   */
  @observable accessor sorting: ScopeChangeRequestSort = {
    field: 'CreateTime',
    direction: 'desc',
  }

  /**
   * Search text, filtering the loaded requests.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Scope Change Requests scroller.
   */
  readonly scroller = new InfiniteScrollProvider<
    ScopeChangeRequest,
    ScopeChangeRequestSearchFilter
  >({
    limit: 20,
    fetchItems: ({ paging, filter, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading change requests failed',
        signal,
      )
      return withRetry(() =>
        this._fetchScopeChangeRequests(
          {
            ...paging,
            workspaceIds: filter?.workspaceIds,
            statuses: filter?.statuses,
            sort: filter && {
              field: filter.sort.field,
              direction:
                filter.sort.direction === 'none'
                  ? undefined
                  : filter.sort.direction,
            },
          },
          signal,
        ),
      )
    },
  })

  /**
   * Search filter changes.
   */
  private readonly _searches$

  constructor(
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _fetchJobs: FetchJobsFn,
    private readonly _filterStorage?: FilterStorage,
  ) {
    this.filters = new FiltersViewModel<ScopeChangeRequestFilter>({
      filterStorage: this._filterStorage,
      definitions: {
        workspaceIds: {
          title: 'Workspaces',
          implicit: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(
                this.getWorkspacesWithScopeChangeRequests(),
              ),
              true,
            ),
        },
        status: {
          icon: 'pending',
          title: 'Quick Select',
          implicit: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              ScopeChangeRequestStatusFilter.pending,
              (signal) => this._fetchStatusOptions(signal),
            ),
        },
      },
    })

    this._searches$ = combineLatest([
      // ...with the remaining filter changes
      this.filters.appliedFilters$,
      // ...and the sort
      toStream(() => this.sorting, true),
    ]).pipe(
      map(([appliedFilters, sort]): ScopeChangeRequestSearchFilter => ({
        workspaceIds: appliedFilters.workspaceIds ?? [],
        statuses: toJobScopeChangeRequestStatus(appliedFilters.status?.type),
        sort,
      })),
      // Ignore emissions with the same search filter.
      distinctUntilChanged<ScopeChangeRequestSearchFilter>(
        ScopeChangeRequestSearchFilter.equals,
      ),
      // Start a search, return the filter that was used.
      switchMap((query): Observable<ScopeChangeRequestSearchResult> =>
        withAbortSignal((signal) => this._searchItems(query, signal)).pipe(
          // The retry alert has reported the failure.
          catchError(() => EMPTY),
        ),
      ),
      switchMap(async (result) => {
        await this._fetchFilterOptions(this._lifecycle.signal)
        return result
      }),
      // Make sure to only subscribe once.
      shareLatest(),
    )

    this._lifecycle.manage(
      this._searches$.pipe(
        // Can't use fromStream because it will subscribe immediately,
        // which we don't want.
        tapAction((search) => (this._lastSearch = search)),
      ),
    )
  }

  /**
   * The internal scope change requests list ran through the transformer.
   */
  @computed
  get scopeChangeRequests() {
    return uniq(this.scroller.items)
  }

  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspaceIds') as SelectFilter<string, Id>
  }

  @computed
  get statusFilter() {
    return this.filters.getFilter('status') as SingleSelectFilter<
      string,
      ScopeChangeRequestStatusFilter
    >
  }

  /**
   * Scope change requests filtered by short id and details
   */
  @computed
  get filteredItems() {
    if (!this.searchText.value) {
      return this.scroller.items
    }

    const text = this.searchText.value.toLowerCase()
    return this.scroller.items.filter(
      (cr) =>
        cr.job.id?.toLowerCase()?.includes(text) ||
        cr.job.shortId.toLowerCase().includes(text) ||
        cr.job.displayName?.toLowerCase()?.includes(text) ||
        cr.workspace.name.toLocaleLowerCase().includes(text),
    )
  }

  private async _searchItems(
    query: ScopeChangeRequestSearchFilter,
    signal?: AbortSignal,
  ) {
    await this.scroller.fetch({ filter: query, signal })
    // Keep loading more until we get something back that isn't being filtered out
    // on the client. If we don't do this here, we potentially display an empty state
    // which does not trigger infinite scroll.
    while (this.scopeChangeRequests.length === 0 && !this.scroller.isDone) {
      signal?.throwIfAborted()
      await this.scroller.more(signal)
    }

    return {
      searchFilter: query,
      filtersApplied: !ScopeChangeRequestSearchFilter.isNone(query),
      scopeChangeRequests: this.scopeChangeRequests,
    }
  }

  /**
   * Refresh status filter
   */
  async refreshStatusFilter() {
    const signal = this._lifecycle.signal
    // The retry alert has reported a failed search.
    await this._searchItems(this._lastSearch.searchFilter, signal).catch(
      () => undefined,
    )
    await this.statusFilter.fetchOptions(signal).catch(() => undefined)
  }

  /**
   * Sets the scope change request sort.
   *
   * @param sortMethod
   */
  @action.bound
  setSortMethod(sortMethod: ScopeChangeRequestSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the scope change request sort.
   *
   * @param direction
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({ field: 'CreateTime', direction: 'none' })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
    })
  }

  /**
   * Sets the scope change request sort.
   *
   * @param field
   */
  @action.bound
  setSortField(field: ScopeChangeRequestSortField) {
    this.setSortMethod({
      field,
      direction: this.sorting.direction,
    })
  }

  /**
   * Workspaces with scope change requests
   */
  getWorkspacesWithScopeChangeRequests(): FilterItem<Id>[] {
    return this._getWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminJobScopeChangeManage)
      .map((mbs) => {
        return { title: mbs.workspace.name, value: mbs.workspace.id }
      })
  }

  /**
   * Fetches the filter options. A failure leaves the previous options, the
   * retry alert having reported it.
   */
  private async _fetchFilterOptions(signal: AbortSignal) {
    await this.workspaceFilter.fetchOptions(signal).catch(() => undefined)
    await this.statusFilter.fetchOptions(signal).catch(() => undefined)
  }

  private async _fetchScopeChangeRequests(
    query: ListScopeChangeRequestsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<ScopeChangeRequest>> {
    const result = await this._scopeChangeRequestStore.listScopeChangeRequests(
      query,
      signal,
    )

    await this._fetchJobs(
      result.data.map((cr) => cr.job.id),
      signal,
    )

    return result
  }

  /** @inheritDoc */
  activate() {
    // The filters restore their stored values on activation, which has to
    // happen before the search pipeline subscribes; the lifecycle tracker
    // would subscribe the pipeline first.
    this.filters.activate()
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.filters.deactivate()
    this._lifecycle.deactivate()
  }

  /**
   * Status list for status filter
   */
  private async _fetchStatusOptions(
    signal?: AbortSignal,
  ): Promise<FilterItem<ScopeChangeRequestStatusFilter>[]> {
    const withRetry = this._createAlertRetryWrapper(
      'Loading change request counts failed',
      signal,
    )
    await withRetry(() =>
      this._scopeChangeRequestStore.fetchScopeChangeRequestStatusAggregations(
        this.workspaceFilter.value,
        signal,
      ),
    )

    return [
      {
        title: 'Total Requests',
        value: {
          type: 'all',
          count: this._getScopeChangeRequestsByStatusCount('all'),
        },
      },
      {
        title: 'Pending review',
        value: {
          type: 'reviewing',
          count: this._getScopeChangeRequestsByStatusCount('reviewing'),
        },
      },
      {
        title: 'Awaiting response',
        value: {
          type: 'awaiting',
          count: this._getScopeChangeRequestsByStatusCount('awaiting'),
        },
      },
      {
        title: 'Update accepted',
        value: {
          type: 'accepted',
          count: this._getScopeChangeRequestsByStatusCount('accepted'),
        },
      },
      {
        title: 'Update rejected',
        value: {
          type: 'rejected',
          count: this._getScopeChangeRequestsByStatusCount('rejected'),
        },
      },
    ]
  }

  /**
   * Scope change requests filtered by status
   * @param status
   */
  private _getScopeChangeRequestsByStatusCount(
    status: ScopeChangeRequestStatusFilterOption,
  ) {
    if (status === 'all') {
      return this._scopeChangeRequestStore.scopeChangeRequestByStatus.reduce(
        (acc, scr) => acc + scr.total,
        0,
      )
    }
    return this._scopeChangeRequestStore.scopeChangeRequestByStatus
      .filter((cr) => toJobScopeChangeRequestStatus(status).includes(cr.status))
      .reduce((acc, src) => acc + src.total, 0)
  }
}

function toJobScopeChangeRequestStatus(
  status?: ScopeChangeRequestStatusFilterOption,
): ScopeChangeRequestStatus[] {
  if (!status) {
    return []
  }

  return match<
    ScopeChangeRequestStatusFilterOption,
    ScopeChangeRequestStatus[]
  >(status, {
    accepted: () => ['Accepted'],
    rejected: () => ['Rejected', 'ApplyFailed'],
    reviewing: () => ['PendingAdminApproval', 'PendingFinalization'],
    awaiting: () => ['PendingClientApproval'],
    all: () => [],
  })
}
